"use client";

import React, { useEffect, useRef, useState } from "react";
import { Search, FileText, Wrench, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  id: "01",
  title: "Consultation",
  description: "Consultation",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/detailing_consultation.png",
  icon: Search
}, {
  id: "02",
  title: "Vehicle Inspection",
  description: "Vehicle Inspection",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/detailing_inspection.png",
  icon: Wrench
}, {
  id: "03",
  title: "Wheels & Arches",
  description: "Wheels & Arches",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/detailing_wheels.png",
  icon: Sparkles
}, {
  id: "04",
  title: "Snow Foam Pre-Wash",
  description: "Snow Foam Pre-Wash",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/detailing_prewash.png",
  icon: FileText
}, {
  id: "05",
  title: "Contact Wash",
  description: "Contact Wash",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/detailing_contactwash.png",
  icon: Search
}, {
  id: "06",
  title: "Chemical Decon",
  description: "Chemical Decon",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/detailing_chemical_decon.png",
  icon: Wrench
}, {
  id: "07",
  title: "Clay Bar Treatment",
  description: "Clay Bar Treatment",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/detailing_claybar.png",
  icon: Sparkles
}, {
  id: "08",
  title: "Drying & Blow Dry",
  description: "Drying & Blow Dry",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/detailing_drying.png",
  icon: FileText
}, {
  id: "09",
  title: "Paint Enhancement",
  description: "Paint Enhancement",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/detailing_paint_enhancement.png",
  icon: Search
}, {
  id: "10",
  title: "Panel Wipe Prep",
  description: "Panel Wipe Prep",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/detailing_panel_wipe.png",
  icon: Wrench
}, {
  id: "11",
  title: "Ceramic Coating",
  description: "Ceramic Coating",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/detailing_ceramic_coating.png",
  icon: Sparkles
}, {
  id: "12",
  title: "Interior Detailing",
  description: "Interior Detailing",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/detailing_interior_detail.png",
  icon: FileText
}, {
  id: "13",
  title: "Final Inspection",
  description: "Final Inspection",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/detailing_final_inspection.png",
  icon: Search
}];
export default function TheFlowMethod() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="131319" id="how-we-bathe-your-car" className="dark bg-background py-24 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl -z-10 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-primary/10 rounded-full blur-3xl -z-10 pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <h2 className="text-sm font-mono font-bold tracking-[0.3em] text-primary uppercase mb-3">
            The Bathers Protocol
          </h2>
          <h3 className="text-4xl md:text-6xl font-serif font-black text-foreground tracking-tighter mb-6 uppercase">
            How We Bathe Your Car
          </h3>
          <p className="text-lg text-muted-foreground font-sans">
            Our proprietary four-stage restoration method engineered for elite automotive preservation and high-gloss performance.
          </p>
        </div>

        <div ref={ref} className="relative max-w-6xl mx-auto">
          {}
          {}
          <div className="hidden md:block absolute top-[220px] left-[12.5%] right-[12.5%] h-1 bg-muted rounded-sm z-0" />
          <div className={cn("hidden md:block absolute top-[220px] left-[12.5%] h-1 bg-primary rounded-sm z-0 transition-all duration-1500 ease-in-out", isVisible ? "w-[75%]" : "w-0")} />

          {}
          <div className="md:hidden absolute left-[39px] top-8 bottom-8 w-1 bg-muted rounded-sm z-0" />
          <div className={cn("md:hidden absolute left-[39px] top-8 w-1 bg-primary rounded-sm z-0 transition-all duration-1500 ease-in-out", isVisible ? "h-[calc(100%-4rem)]" : "h-0")} />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
            {steps.map((step, i) => {
            const Icon = step.icon;
            const animationDelay = `${i * 200}ms`;
            return <div key={step.id} data-index={i} className="relative flex flex-col md:items-center group">
                  {}
                  <div className="flex flex-row md:flex-col items-start md:items-center gap-6 md:gap-8 w-full">
                    
                    {}
                    <div className="shrink-0 md:w-full flex justify-center">
                      <div className="relative w-20 h-20 md:w-40 md:h-40 rounded-sm p-0.5 bg-secondary/50 backdrop-blur-md border border-border shadow-2xl overflow-hidden group-hover:border-primary/60 transition-colors duration-500">
                        <img src={step.image} alt={step.title} className="w-full h-full object-cover rounded-sm desaturate group-hover:desaturate-0 transition-all duration-700" loading="lazy" />
                        <div className="absolute inset-0 bg-primary/10 rounded-sm mix-blend-overlay" />
                      </div>
                    </div>

                    {}
                    <div className="flex flex-col md:items-center flex-1 pt-2 md:pt-0">
                      
                      {}
                      <div className="absolute left-6 md:static md:left-auto w-14 h-14 rounded-sm bg-background border-2 border-muted shadow-lg flex items-center justify-center z-10 shrink-0 mb-6 md:mb-8">
                        <div className={cn("w-full h-full rounded-sm flex items-center justify-center bg-secondary text-muted-foreground transition-all duration-500", isVisible && "bg-primary text-primary-foreground shadow-[0_0_25px_rgba(0,255,255,0.4)]")}>
                          <Icon className="w-5 h-5" />
                        </div>
                        {}
                        {isVisible && <div className="absolute inset-0 rounded-sm border border-primary animate-ping opacity-30" style={{
                      animationDelay
                    }} />}
                      </div>

                      {}
                      <div className="md:text-center">
                        <div className="flex items-center md:justify-center gap-2 mb-2">
                          <span className="text-sm font-mono font-bold text-primary">
                            {step.id}.
                          </span>
                          <h4 className="text-xl font-serif font-bold text-foreground uppercase tracking-tight">
                            {step.title}
                          </h4>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed font-sans">
                          {step.description}
                        </p>
                      </div>

                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}